/**
 * Bots Page
 * Manage lending bot instances
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { botsService, type BotInstance, type CreateBotRequest } from '../services/bots.service'
import { bitfinexApiKeysService } from '../services/bitfinex-api-keys.service'
import { formatDate } from '../utils/formatters'
import { CreateBotForm } from '../components/Bots/CreateBotForm'
import { BotErrorPanel } from '../components/Bots/BotErrorPanel'
import { ConfirmDialog } from '../components/Common/ConfirmDialog'

export default function BotsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; name: string } | null>(null)

  // Fetch bots
  const { data: bots = [], isLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: () => botsService.list(),
  })

  // Fetch Bitfinex API keys
  const { data: apiKeys = [] } = useQuery({
    queryKey: ['bitfinexApiKeys'],
    queryFn: () => bitfinexApiKeysService.list(),
  })

  const activeApiKeys = apiKeys.filter((key) => key.status === 'active')

  // Create bot mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateBotRequest) => botsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] })
      setShowCreateModal(false)
    },
  })

  // Delete bot mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => botsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] })
      setDeleteConfirm(null)
    },
  })

  // Start bot mutation
  const startMutation = useMutation({
    mutationFn: (id: number) => botsService.start(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] })
    },
  })

  // Stop bot mutation
  const stopMutation = useMutation({
    mutationFn: (id: number) => botsService.stop(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] })
    },
  })

  const handleDelete = (id: number, name: string) => {
    setDeleteConfirm({ id, name })
  }

  const handleConfirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.id)
    }
  }

  const handleStart = (id: number) => {
    startMutation.mutate(id)
  }

  const handleStop = (id: number) => {
    stopMutation.mutate(id)
  }

  const activeBots = bots.filter((bot) => bot.status === 'active').length

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Bot 管理</h1>
            <p style={styles.subtitle}>
              管理自動放貸機器人實例 · {activeBots} 個運行中
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={styles.createButton}
            disabled={activeApiKeys.length === 0}
          >
            + 建立 Bot
          </button>
        </div>

        {activeApiKeys.length === 0 && (
          <div style={styles.warningBox}>
            <span style={styles.warningIcon}>⚠️</span>
            <div>
              <p style={styles.warningTitle}>需要 Bitfinex API Key</p>
              <p style={styles.warningText}>
                建立 Bot 前，請先新增 Bitfinex API Key。您可以在 Bitfinex 帳號設定中取得 API 憑證。
              </p>
              <p style={styles.warningText}>(Bitfinex API Key 管理功能開發中...)</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={styles.loading}>載入中...</div>
        ) : bots.length === 0 ? (
          <div style={styles.empty}>
            <p style={styles.emptyText}>尚無 Bot 實例</p>
            <p style={styles.emptySubtext}>
              {activeApiKeys.length > 0
                ? '建立第一個 Bot 開始自動放貸'
                : '請先新增 Bitfinex API Key'}
            </p>
          </div>
        ) : (
          <div style={styles.botsGrid}>
            {bots.map((bot) => (
              <BotCard
                key={bot.id}
                bot={bot}
                onStart={handleStart}
                onStop={handleStop}
                onDelete={handleDelete}
                onMonitor={(id) => navigate(`/bots/${id}/monitor`)}
                onDashboard={(id) => navigate(`/bots/${id}/dashboard`)}
                isStarting={startMutation.isPending}
                isStopping={stopMutation.isPending}
                isDeleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )}

        {showCreateModal && activeApiKeys.length > 0 && (
          <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
            <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
              <CreateBotForm
                apiKeys={activeApiKeys.map((key) => ({
                  id: key.id,
                  label: key.label || `API Key #${key.id}`,
                }))}
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowCreateModal(false)}
                isLoading={createMutation.isPending}
              />
            </div>
          </div>
        )}

        {deleteConfirm && (
          <ConfirmDialog
            title="刪除 Bot"
            message={`確定要刪除 Bot「${deleteConfirm.name}」嗎？此操作無法復原。`}
            confirmLabel="刪除"
            cancelLabel="取消"
            confirmStyle="danger"
            onConfirm={handleConfirmDelete}
            onCancel={() => setDeleteConfirm(null)}
            isLoading={deleteMutation.isPending}
          />
        )}
      </div>
    </Layout>
  )
}

/**
 * Bot Card Component
 */
function BotCard({
  bot,
  onStart,
  onStop,
  onDelete,
  onMonitor,
  onDashboard,
  isStarting,
  isStopping,
  isDeleting,
}: {
  bot: BotInstance
  onStart: (id: number) => void
  onStop: (id: number) => void
  onDelete: (id: number, name: string) => void
  onMonitor: (id: number) => void
  onDashboard: (id: number) => void
  isStarting: boolean
  isStopping: boolean
  isDeleting: boolean
}) {
  const statusColor = {
    active: '#10b981',
    paused: '#f59e0b',
    stopped: '#6b7280',
    error: '#ef4444',
  }[bot.status]

  const statusText = {
    active: '運行中',
    paused: '已暫停',
    stopped: '已停止',
    error: '錯誤',
  }[bot.status]

  return (
    <div style={styles.botCard}>
      <div style={styles.botHeader}>
        <div>
          <h3 style={styles.botName}>{bot.name}</h3>
          <p style={styles.botCurrency}>{bot.currency}</p>
        </div>
        <span style={{ ...styles.statusBadge, backgroundColor: statusColor }}>{statusText}</span>
      </div>

      <div style={styles.botStats}>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>總放貸</span>
          <span style={styles.statValue}>${bot.total_lent.toFixed(2)}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>總收益</span>
          <span style={styles.statValue}>${bot.total_earned.toFixed(2)}</span>
        </div>
      </div>

      {bot.last_cycle_at && (
        <p style={styles.lastCycle}>最後運行：{formatDate(bot.last_cycle_at)}</p>
      )}

      {bot.error_message && (
        <BotErrorPanel errorMessage={bot.error_message} onRetry={() => onStart(bot.id)} />
      )}

      <div style={styles.botActions}>
        <button
          onClick={() => onDashboard(bot.id)}
          style={styles.dashboardButton}
        >
          📈 Dashboard
        </button>
        <button
          onClick={() => onMonitor(bot.id)}
          style={styles.monitorButton}
        >
          📊 日誌
        </button>
        {bot.status === 'active' || bot.status === 'paused' ? (
          <button
            onClick={() => onStop(bot.id)}
            style={styles.stopButton}
            disabled={isStopping}
          >
            停止
          </button>
        ) : (
          <button
            onClick={() => onStart(bot.id)}
            style={styles.startButton}
            disabled={isStarting}
          >
            啟動
          </button>
        )}
        <button
          onClick={() => onDelete(bot.id, bot.name)}
          style={styles.deleteButton}
          disabled={isDeleting || bot.status === 'active'}
        >
          刪除
        </button>
      </div>

      {bot.api_key_label && (
        <p style={styles.apiKeyLabel}>API Key: {bot.api_key_label}</p>
      )}
    </div>
  )
}


const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  createButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  warningBox: {
    padding: '16px',
    backgroundColor: '#fef3c7',
    borderRadius: '5px',
    marginBottom: '30px',
    display: 'flex',
    gap: '12px',
    border: '1px solid #fbbf24',
  },
  warningIcon: {
    fontSize: '20px',
  },
  warningTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#92400e',
    marginBottom: '4px',
  },
  warningText: {
    fontSize: '13px',
    color: '#92400e',
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    fontSize: '16px',
    color: '#666',
  },
  empty: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '60px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '18px',
    color: '#333',
    marginBottom: '8px',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#666',
  },
  botsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px',
  },
  botCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  botHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  botName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '4px',
  },
  botCurrency: {
    fontSize: '14px',
    color: '#666',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    color: 'white',
    fontWeight: '500',
  },
  botStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  statLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
  },
  lastCycle: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '12px',
  },
  botActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  dashboardButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  monitorButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#8b5cf6',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  startButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  stopButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#f59e0b',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  deleteButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  apiKeyLabel: {
    fontSize: '11px',
    color: '#999',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #f3f4f6',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '0',
    maxWidth: '900px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
}
